import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { media } from "@atlaskit/primitives/responsive";

import {
    collectionGuideSectionRelayFragment$data,
    collectionGuideSectionRelayFragment$key
} from "@/__generated__/collectionGuideSectionRelayFragment.graphql";
import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";
import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics";
import { SanitizedRichText } from "@/components/confluence/util/sanitized-rich-text";
import { useLocalizedHref } from "@/libraries/web-framework";

import { TemplateCard } from "../components/template-card-relay";

interface CollectionDetailStepProps {
    step: NonNullable<
        collectionGuideSectionRelayFragment$data["collectionSteps"]
    >[number];
}

interface CollectionDetailStepsProps {
    collectionGuide: collectionGuideSectionRelayFragment$key;
}

const guideSectionStyle = css({
    marginBlockStart: 80,
    marginInlineStart: "auto",
    marginInlineEnd: "auto"
});

const stepContainerStyle = css({
    "display": "flex",
    "justifyContent": "space-between",
    "marginBlockEnd": 80,
    "gap": "2.5rem",

    [media.below.sm]: {
        flexDirection: "column",
        gap: "2rem"
    },

    "&:last-of-type": {
        marginBlockEnd: 0
    }
});

const guideSectionHeading = css({
    "fontSize": "2.25rem",
    "lineHeight": "2.75rem",
    "fontWeight": 500,
    "letterSpacing": 0.3,
    "color": "#1d3059",
    "fontFamily": "var(--bxp-charlie-display-font-family)",
    "margin": 0,
    "marginBlockEnd": 40,

    "@media (max-width: 47.99rem)": {
        fontSize: "1.5rem",
        lineHeight: "1.75rem"
    }
});

const stepHeading = css({
    fontSize: "1.5rem",
    lineHeight: "2rem",
    fontWeight: 500,
    letterSpacing: 0.3,
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#1d3059",
    margin: 0,
    marginBlockEnd: 32,

    [media.below.sm]: {
        fontSize: "1.25rem",
        lineHeight: "1.5rem"
    }
});
const stepSubHeading = css({
    fontSize: "1.25rem",
    lineHeight: "1.5rem",
    fontWeight: 500,
    letterSpacing: 0.3,
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#1d3059",
    margin: 0,
    marginBlockEnd: 24,

    [media.below.sm]: {
        fontSize: "1rem",
        lineHeight: "1.25rem"
    }
});

const stepContentWrapper = css({
    display: "flex",
    flexDirection: "column"
});

const stepCardWrapper = css({
    flexGrow: 0,
    flexShrink: 0,
    textAlign: "left",
    margin: "0 auto"
});

const stepTemplateLinkStyle = css({
    "marginBlockStart": 24,
    "color": "#0052cc",
    "fontWeight": 500,
    "textDecoration": "underline",

    ":hover": {
        // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#0065ff",
        textDecoration: "none"
    },
    ":active": {
        textDecoration: "none"
    },
    ":focus": {
        textDecoration: "none"
    },
    ":visited": {
        // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#0052cc"
    }
});

const CollectionDetailStep = ({ step }: CollectionDetailStepProps) => {
    const stepTemplateLink = useLocalizedHref(
        `/software/confluence/templates/${step.relatedTemplate?.urlSlug}`
    );
    return (
        <div css={stepContainerStyle}>
            {step.relatedTemplate && (
                <div css={stepCardWrapper}>
                    <TemplateCard
                        template={step.relatedTemplate}
                        background={
                            "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg1.svg)"
                        }
                    />
                </div>
            )}
            <div css={stepContentWrapper}>
                <h3 css={stepHeading}>{step.name}</h3>
                <h4 css={stepSubHeading}>{step.stepSubheading}</h4>
                {step.stepDescription && (
                    <SanitizedRichText innerHTML={step.stepDescription} />
                )}
                <a
                    css={stepTemplateLinkStyle}
                    href={stepTemplateLink}
                    {...ConfluenceTemplateAnalytics.viewTemplateAnalyticProps(
                        step.relatedTemplate?.urlSlug
                    )}
                >
                    <FormattedMessage
                        {...ConfluenceTemplateMessages.collectionViewTemplate}
                    />
                    {" →"}
                </a>
            </div>
        </div>
    );
};

const CollectionGuideSectionRelayFragment = graphql`
    fragment collectionGuideSectionRelayFragment on ContentPlatformTemplateCollectionGuide {
        name
        collectionSteps {
            name
            stepSubheading
            stepDescription
            relatedTemplate {
                urlSlug
                ...templateCardRelayFragment
            }
        }
    }
`;

export const CollectionGuideSection = ({
    collectionGuide
}: CollectionDetailStepsProps) => {
    const { name, collectionSteps } = useFragment(
        CollectionGuideSectionRelayFragment,
        collectionGuide
    );
    return (
        <section css={guideSectionStyle}>
            <h2 css={guideSectionHeading}>{name}</h2>
            {collectionSteps?.map((step, index) => (
                <CollectionDetailStep key={`step-${index}`} step={step} />
            ))}
        </section>
    );
};
